import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'
import {
  EMPTY_OVERRIDE,
  Field,
  billingModeLabel,
  contractFromApiRow,
  displayDateTime,
  effectiveStateLabel,
  overrideFromApiRow,
  priceModeLabel,
  seatCountPolicyLabel,
  snapshotStatusLabel,
  statusLabel,
} from './billing/orgBillingUtils'

export default function BillingOrgContractDetailPage() {
  const { contractId: contractIdParam } = useParams()
  const contractId = Number(contractIdParam)
  const { show } = useToast()

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingContract, setEditingContract] = useState(false)
  const [contractDraft, setContractDraft] = useState(null)
  const [orgContracts, setOrgContracts] = useState([])
  const [pricingOverrides, setPricingOverrides] = useState([])
  const [plans, setPlans] = useState([])

  const [overrideForm, setOverrideForm] = useState(EMPTY_OVERRIDE)
  const [overridePanelOpen, setOverridePanelOpen] = useState(false)

  const [terminationLoading, setTerminationLoading] = useState(false)
  const [terminationPreview, setTerminationPreview] = useState(null)
  const [terminationSnapshots, setTerminationSnapshots] = useState([])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [plansRes, contractsRes, overridesRes] = await Promise.all([
        api.get('/billing/admin/config/plans'),
        api.get('/billing/admin/config/org-contracts'),
        api.get('/billing/admin/config/org-pricing-overrides'),
      ])
      setPlans(Array.isArray(plansRes?.data?.data) ? plansRes.data.data : [])
      setOrgContracts(Array.isArray(contractsRes?.data?.data) ? contractsRes.data.data : [])
      setPricingOverrides(Array.isArray(overridesRes?.data?.data) ? overridesRes.data.data : [])
    } catch (e) {
      show('계약 정보를 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    loadData()
  }, [loadData])

  const contract = useMemo(
    () => orgContracts.find((c) => Number(c.id) === contractId) || null,
    [orgContracts, contractId],
  )

  const contractOverrides = useMemo(
    () => pricingOverrides.filter((o) => Number(o.orgContractId) === contractId),
    [pricingOverrides, contractId],
  )

  useEffect(() => {
    if (!contract || editingContract) return
    setContractDraft(contractFromApiRow(contract))
  }, [contract, editingContract])

  const loadTerminationSnapshots = async () => {
    if (!contractId) return []
    try {
      const res = await api.get(`/billing/admin/config/org-contracts/${contractId}/termination-snapshots`)
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      setTerminationSnapshots(rows)
      return rows
    } catch (e) {
      show('종료 스냅샷을 불러오지 못했습니다.', 'error')
      return []
    }
  }

  const previewTermination = async () => {
    if (!contract) return
    setTerminationLoading(true)
    try {
      const res = await api.post(`/billing/admin/config/org-contracts/${contract.id}/termination-preview`, {
        issueTransitionPromotion: true,
        transitionDiscountPercent: 30,
        transitionPromotionValidDays: 30,
      })
      setTerminationPreview(res?.data?.data ?? null)
      await loadTerminationSnapshots()
      show('종료 미리보기를 불러왔습니다.', 'success')
    } catch (e) {
      show('종료 미리보기에 실패했습니다.', 'error')
    } finally {
      setTerminationLoading(false)
    }
  }

  const executeTermination = async () => {
    if (!contract) return
    const ok = window.confirm('이 조직 계약을 종료 처리하고 대상 사용자를 개인 과금 기준으로 전환하시겠습니까?')
    if (!ok) return
    setTerminationLoading(true)
    try {
      const res = await api.post(`/billing/admin/config/org-contracts/${contract.id}/terminate`, {
        issueTransitionPromotion: true,
        transitionDiscountPercent: 30,
        transitionPromotionValidDays: 30,
      })
      setTerminationPreview(res?.data?.data ?? null)
      await loadData()
      await loadTerminationSnapshots()
      show('조직 계약 종료 처리가 완료되었습니다.', 'success')
    } catch (e) {
      show('조직 계약 종료 처리에 실패했습니다.', 'error')
    } finally {
      setTerminationLoading(false)
    }
  }

  const saveContract = async () => {
    if (!contractDraft?.orgId) {
      show('조직 정보가 없습니다.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        orgId: Number(contractDraft.orgId),
        billingMode: contractDraft.billingMode,
        appliedPlanCode: contractDraft.appliedPlanCode,
        seatCountPolicy: contractDraft.seatCountPolicy,
        contractedSeats: Number(contractDraft.contractedSeats) || 0,
        status: contractDraft.status,
        contactEmail: contractDraft.contactEmail || null,
        contactPhone: contractDraft.contactPhone || null,
        notes: contractDraft.notes || null,
        startedAt: contractDraft.startedAt || null,
        endsAt: contractDraft.endsAt || null,
        graceUntil: contractDraft.graceUntil || null,
      }
      await api.put(`/billing/admin/config/org-contracts/${contractId}`, payload)
      show('조직 계약이 수정되었습니다.', 'success')
      setEditingContract(false)
      await loadData()
    } catch (e) {
      show('조직 계약 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const saveOverride = async () => {
    const ocId = Number(overrideForm.orgContractId || contractId)
    if (!Number.isFinite(ocId) || ocId <= 0) {
      show('조직 계약을 선택해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        orgContractId: ocId,
        planCode: overrideForm.planCode,
        priceMode: overrideForm.priceMode,
        discountRate: Number(overrideForm.discountRate) || 0,
        fixedPrice: Number(overrideForm.fixedPrice) || 0,
        seatFrom: Number(overrideForm.seatFrom) || 0,
        seatTo: Number(overrideForm.seatTo) || 0,
        priorityNo: Number(overrideForm.priorityNo) || 0,
        startedAt: overrideForm.startedAt || null,
        endsAt: overrideForm.endsAt || null,
      }
      if (overrideForm.id) {
        await api.put(`/billing/admin/config/org-pricing-overrides/${overrideForm.id}`, payload)
        show('조직 가격정책이 수정되었습니다.', 'success')
      } else {
        await api.post('/billing/admin/config/org-pricing-overrides', payload)
        show('조직 가격정책이 등록되었습니다.', 'success')
      }
      setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
      setOverridePanelOpen(false)
      loadData()
    } catch (e) {
      show('조직 가격정책 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const startEditOverride = (override) => {
    setOverrideForm(overrideFromApiRow(override))
    setOverridePanelOpen(true)
  }


  const beginContractEdit = () => {
    if (!contract) return
    setContractDraft(contractFromApiRow(contract))
    setEditingContract(true)
  }

  const cancelContractEdit = () => {
    if (contract) setContractDraft(contractFromApiRow(contract))
    setEditingContract(false)
  }

  if (loading && orgContracts.length === 0) {
    return <section className="py-10 text-center text-slate-500">로딩 중...</section>
  }

  if (!Number.isFinite(contractId) || contractId <= 0) {
    return (
      <section className="space-y-4">
        <p className="text-slate-600">잘못된 계약 번호입니다.</p>
        <Link to=".." className="text-sm text-brand-navy underline">
          목록으로
        </Link>
      </section>
    )
  }

  if (!loading && !contract) {
    return (
      <section className="space-y-4">
        <p className="text-slate-600">해당 조직 계약을 찾을 수 없습니다.</p>
        <Link to=".." className="text-sm text-brand-navy underline">
          목록으로
        </Link>
      </section>
    )
  }

  const d = contractDraft || (contract ? contractFromApiRow(contract) : null)

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to=".." className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
          ← 목록
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold text-slate-900">조직 계약 #{contractId}</h1>
          <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-02" />
        </div>
      </div>
      {contract ? (
        <p className="text-slate-600">
          {contract.orgName} <span className="text-slate-400">(조직 ID {contract.orgId})</span>
        </p>
      ) : null}

      <div className="rounded-lg border bg-white p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-l-4 border-brand-navy pl-3 text-lg font-semibold text-slate-900">계약 정보</h2>
          {!editingContract ? (
            <button type="button" className="rounded border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={beginContractEdit}>
              편집
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="rounded border px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={cancelContractEdit} disabled={saving}>
                취소
              </button>
              <button
                type="button"
                className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
                onClick={saveContract}
                disabled={saving}
              >
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          )}
        </div>

        {!editingContract && contract ? (
          <dl className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            <div>
              <dt className="font-medium text-slate-700">과금 모델</dt>
              <dd className="mt-1 text-slate-900">{billingModeLabel(contract.billingMode)}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">적용 플랜</dt>
              <dd className="mt-1 text-slate-900">{contract.appliedPlanCode || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">상태</dt>
              <dd className="mt-1 text-slate-900">
                {statusLabel(contract.status)}
                <span className="ml-2 text-xs text-slate-500">
                  {effectiveStateLabel(contract)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">좌석 정책</dt>
              <dd className="mt-1 text-slate-900">{seatCountPolicyLabel(contract.seatCountPolicy)}</dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">계약 좌석 / 승인 인원</dt>
              <dd className="mt-1 text-slate-900">
                {contract.contractedSeats || 0} / {contract.currentApprovedMembers ?? 0}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">연락</dt>
              <dd className="mt-1 text-slate-900">
                {contract.contactEmail || '-'} · {contract.contactPhone || '-'}
              </dd>
            </div>
            <div className="md:col-span-3">
              <dt className="font-medium text-slate-700">계약 기간</dt>
              <dd className="mt-1 text-slate-900">
                시작 {displayDateTime(contract.startedAt)} · 종료 {displayDateTime(contract.endsAt)} · 유예 {displayDateTime(contract.graceUntil)}
              </dd>
            </div>
            <div className="md:col-span-3">
              <dt className="font-medium text-slate-700">메모</dt>
              <dd className="mt-1 rounded border border-slate-100 bg-slate-50 px-3 py-2 text-slate-800">{contract.notes || '—'}</dd>
            </div>
          </dl>
        ) : d ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="조직">
              <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {contract?.orgName} (#{contract?.orgId})
              </div>
            </Field>
            <Field label="과금 모델">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.billingMode}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, billingMode: e.target.value }))}
              >
                <option value="ORG_PAYS">조직 결제 (단체 계약)</option>
              </select>
            </Field>
            <Field label="적용 플랜">
              <select
                aria-label="조직 계약 적용 플랜"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.appliedPlanCode}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, appliedPlanCode: e.target.value }))}
              >
                {plans.map((plan) => (
                  <option key={plan.planCode} value={plan.planCode}>
                    {plan.planCode}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="상태">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.status}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="ACTIVE">활성</option>
                <option value="TERMINATING">종료 예정(유예)</option>
                <option value="INACTIVE">비활성</option>
              </select>
            </Field>
            <Field label="좌석 정책">
              <select
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.seatCountPolicy}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, seatCountPolicy: e.target.value }))}
              >
                <option value="APPROVED_MEMBER_COUNT">승인된 멤버 수 기준</option>
                <option value="CONTRACTED_SEATS">계약 좌석 수 기준</option>
              </select>
            </Field>
            <Field label="계약 좌석 수">
              <input
                type="number"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contractedSeats}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contractedSeats: e.target.value }))}
              />
            </Field>
            <Field label="연락 이메일">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contactEmail}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contactEmail: e.target.value }))}
              />
            </Field>
            <Field label="연락 전화번호">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.contactPhone}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, contactPhone: e.target.value }))}
              />
            </Field>
            <Field label="계약 시작일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.startedAt}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, startedAt: e.target.value }))}
              />
            </Field>
            <Field label="계약 종료일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.endsAt}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, endsAt: e.target.value }))}
              />
            </Field>
            <Field label="유예 종료일">
              <input
                type="datetime-local"
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.graceUntil}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, graceUntil: e.target.value }))}
              />
            </Field>
            <Field label="메모">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={d.notes}
                onChange={(e) => setContractDraft((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Field>
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border bg-white p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="border-l-4 border-brand-navy pl-3 text-lg font-semibold text-slate-900">가격정책</h2>
          <button
            type="button"
            className="rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
            onClick={() => {
              setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
              setOverridePanelOpen(true)
            }}
          >
            가격정책 추가
          </button>
        </div>

        {overridePanelOpen ? (
          <div className="mb-6 rounded border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">{overrideForm.id ? '가격정책 수정' : '가격정책 등록'}</h3>
              <button
                type="button"
                className="text-sm text-slate-600 hover:text-slate-900"
                onClick={() => {
                  setOverridePanelOpen(false)
                  setOverrideForm({ ...EMPTY_OVERRIDE, orgContractId: String(contractId) })
                }}
              >
                닫기
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="조직 계약">
                <div className="rounded border bg-slate-100 px-3 py-2 text-sm text-slate-600">#{contractId}</div>
              </Field>
              <Field label="플랜">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.planCode}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, planCode: e.target.value }))}
                >
                  {plans.map((plan) => (
                    <option key={plan.planCode} value={plan.planCode}>
                      {plan.planCode}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="가격 정책">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.priceMode}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, priceMode: e.target.value }))}
                >
                  <option value="PERCENT_DISCOUNT">정률 할인(%)</option>
                  <option value="FIXED_PRICE">고정 단가</option>
                </select>
              </Field>
              <Field label="할인율(%)">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.discountRate}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, discountRate: e.target.value }))}
                />
              </Field>
              <Field label="고정 가격">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.fixedPrice}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, fixedPrice: e.target.value }))}
                />
              </Field>
              <Field label="우선순위">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.priorityNo}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, priorityNo: e.target.value }))}
                />
              </Field>
              <Field label="좌석 구간 시작">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.seatFrom}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, seatFrom: e.target.value }))}
                />
              </Field>
              <Field label="좌석 구간 끝">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.seatTo}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, seatTo: e.target.value }))}
                />
              </Field>
              <Field label="적용 시작일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.startedAt}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, startedAt: e.target.value }))}
                />
              </Field>
              <Field label="적용 종료일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={overrideForm.endsAt}
                  onChange={(e) => setOverrideForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                />
              </Field>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
                onClick={saveOverride}
                disabled={saving}
              >
                {saving ? '저장 중…' : overrideForm.id ? '가격정책 수정' : '가격정책 등록'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">플랜</th>
                <th className="px-4 py-3 text-left">정책</th>
                <th className="px-4 py-3 text-left">가격</th>
                <th className="px-4 py-3 text-left">좌석 구간</th>
                <th className="px-4 py-3 text-left">적용 기간</th>
                <th className="px-4 py-3 text-left"> </th>
              </tr>
            </thead>
            <tbody>
              {contractOverrides.length === 0 && (
                <tr className="border-t">
                  <td className="px-4 py-6 text-slate-500" colSpan={6}>
                    이 계약에 등록된 가격정책이 없습니다.
                  </td>
                </tr>
              )}
              {contractOverrides.map((item) => (
                <tr key={item.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{item.planCode}</td>
                  <td className="px-4 py-3">{priceModeLabel(item.priceMode)}</td>
                  <td className="px-4 py-3">
                    {item.priceMode === 'FIXED_PRICE' ? `₩${Number(item.fixedPrice || 0).toLocaleString()}` : `${Number(item.discountRate || 0)}%`}
                  </td>
                  <td className="px-4 py-3">
                    {item.seatFrom} ~ {item.seatTo}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>시작 {displayDateTime(item.startedAt)}</div>
                    <div>종료 {displayDateTime(item.endsAt)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <button type="button" className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50" onClick={() => startEditOverride(item)}>
                      수정
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-6">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">계약 종료</h2>
        <p className="mb-4 text-sm text-slate-600">종료 미리보기로 영향 사용자를 확인한 뒤 종료 처리할 수 있습니다.</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={previewTermination}
            disabled={terminationLoading || !contract}
          >
            종료 미리보기
          </button>
          {contract?.status !== 'INACTIVE' ? (
            <button
              type="button"
              className="rounded border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              onClick={executeTermination}
              disabled={terminationLoading}
            >
              종료 처리
            </button>
          ) : null}
          <button
            type="button"
            className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            onClick={loadTerminationSnapshots}
            disabled={terminationLoading}
          >
            스냅샷 보기
          </button>
        </div>

        {terminationPreview?.contract?.id === contractId ? (
          <div className="mt-4 space-y-2 text-sm">
            <div className="font-semibold text-slate-900">영향 사용자 미리보기</div>
            <div>대상 인원: {terminationPreview?.affectedUserCount || 0}명</div>
            <div>
              전환 할인: {terminationPreview?.transitionDiscountPercent || 0}% / 유효 {terminationPreview?.transitionPromotionValidDays || 0}일
            </div>
            <div className="overflow-x-auto rounded border border-amber-100 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">이름</th>
                    <th className="px-3 py-2 text-left">이메일</th>
                    <th className="px-3 py-2 text-left">이전</th>
                    <th className="px-3 py-2 text-left">전환 후</th>
                  </tr>
                </thead>
                <tbody>
                  {(terminationPreview?.affectedUsers || terminationPreview?.processedUsers || []).map((item) => (
                    <tr key={item.userId} className="border-t border-amber-50">
                      <td className="px-3 py-2">{item.userName}</td>
                      <td className="px-3 py-2">{item.userEmail}</td>
                      <td className="px-3 py-2">
                        {item.beforeBillingMode} / {item.beforePlanCode}
                      </td>
                      <td className="px-3 py-2">
                        {item.targetBillingMode} / {item.targetPlanCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {terminationSnapshots.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-sm font-medium text-slate-800">저장된 종료 스냅샷</div>
            <div className="overflow-x-auto rounded border border-amber-100 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">이름</th>
                    <th className="px-3 py-2 text-left">이메일</th>
                    <th className="px-3 py-2 text-left">전환 플랜</th>
                    <th className="px-3 py-2 text-left">쿠폰</th>
                    <th className="px-3 py-2 text-left">상태</th>
                    <th className="px-3 py-2 text-left">처리 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {terminationSnapshots.map((item) => (
                    <tr key={item.id} className="border-t border-amber-50">
                      <td className="px-3 py-2">{item.userName}</td>
                      <td className="px-3 py-2">{item.userEmail}</td>
                      <td className="px-3 py-2">
                        {item.targetBillingMode} / {item.targetPlanCode}
                      </td>
                      <td className="px-3 py-2">{item.transitionPromotionCode || '-'}</td>
                      <td className="px-3 py-2">{snapshotStatusLabel(item.processingStatus)}</td>
                      <td className="px-3 py-2">{displayDateTime(item.processedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
